import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { PromisePool } from 'es6-promise-pool';
import Select from 'react-select';
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { APIupdateFileOwner } from '../api/Files';
import { APIupdateCollectionOwner } from '../api/Collections';
import { APIgetUsers } from '../api/User';

import { updateCollectionOnCollectionsList } from '../actions/Collections';
import {
  updateFilesOnFilesList,
  updateFilesOnSelectedFilesList,
} from '../actions/Files';

import ShareModalInviteUserSelectItem from './ModalUserSelectItem';
import SelectVirtualMenuList from './SelectVirtualMenuList';
import ProcessingProgress from './ProcessingProgress';
import ProcessingSpinner from './ProcessingSpinner';

import { addFlashMessage } from '../helpers/FlashMessages';

import ErrorBoundary from './ErrorBoundary';

class ChangeOwnerModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      mode: null,
      users: [],
      selectedUser: null,
      updateCollectionFiles: false,
      updatedItems: 0,
      usersLoading: false,
      collectionsLoading: false,
      saving: false,
    };

    this.handleUpdate = this.handleUpdate.bind(this);
    this.handleUserChange = this.handleUserChange.bind(this);
    this.handleCollectionFilesChange = this.handleCollectionFilesChange.bind(this);
  }

  componentDidMount() {
    this.setInitialState();
    this.loadUsers();
  }

  getUsersOptions() {
    const { users } = this.state;

    const options = [];
    users
      .filter(u => (!u.deactivated_at))
      .forEach((user) => {
        options.push({
          uuid: user.uuid,
          email: user.email,
          name: user.name,
          avatar: user.avatar_url,
        });
      });

    return options;
  }

  setInitialState() {
    const {
      files,
      collections,
    } = this.props;

    if (collections.length > 0) {
      this.setState({ mode: 'collections' });
    } else if (files.length > 0) {
      this.setState({ mode: 'files' });
    }
  }

  loadUsers() {
    this.setState({ usersLoading: true });

    APIgetUsers().then((response) => {
      const { data } = response;
      if (data) {
        this.setState({ users: data });
      }
      this.setState({ usersLoading: false });
    });
  }

  handleUpdate() {
    const {
      intl,
      files,
      collections,
      handleClose,
      updateFileOnList,
      updateCollectionOnList,
    } = this.props;
    const {
      mode,
      selectedUser,
      updateCollectionFiles,
    } = this.state;

    this.setState({ saving: true });

    let items = [];
    if (mode === 'files') {
      items = items.concat(files.map(f => ({ type: 'file', uuid: f })));
    }

    if (mode === 'collections') {
      items = items.concat(collections.map(c => ({ type: 'collection', uuid: c.uuid })));
    }
    this.setState({ total: items.length });

    let counter = 0;
    const promiseProducer = () => {
      if (counter < items.length) {
        const item = items[counter];
        counter += 1;
        if (item.type === 'file') {
          return APIupdateFileOwner(item.uuid, selectedUser.uuid).then((response) => {
            this.setState({ updatedItems: counter });
            updateFileOnList(response.data);
          });
        }
        if (item.type === 'collection') {
          return APIupdateCollectionOwner(item.uuid, selectedUser.uuid, updateCollectionFiles)
            .then((response) => {
              this.setState({ updatedItems: counter });
              updateCollectionOnList(response.data);
            });
        }
      }
      return null;
    };

    const pool = new PromisePool(promiseProducer, 2);
    pool.start().then(() => {
      addFlashMessage({
        type: 'success',
        text: intl.formatMessage({
          defaultMessage: 'Your changes have been saved',
        }),
      });
      handleClose();
    });
  }

  handleUserChange(value) {
    this.setState({ selectedUser: value });
  }

  handleCollectionFilesChange(e) {
    const { target } = e;
    this.setState({ updateCollectionFiles: target.checked });
  }

  render() {
    const {
      intl,
      files,
      collections,
      handleClose,
    } = this.props;
    const {
      mode,
      selectedUser,
      total,
      updatedItems,
      usersLoading,
      collectionsLoading,
      saving,
    } = this.state;
    const loading = usersLoading || collectionsLoading;

    if (!mode) {
      return null;
    }

    return (
      <ErrorBoundary>
        <ModalHeader>
          Change ownership
        </ModalHeader>
        <ModalBody style={{ overflow: 'visible' }}>
          {mode === 'files' && (
            <p>
              {intl.formatMessage({
                defaultMessage: `Change the owner of {filesCount} 
                {filesCount, plural, one {file} other {files}}`,
              }, { filesCount: files.length })}
            </p>
          )}
          {mode === 'collections' && (
            <p>
              {collections.length === 1 ? (
                intl.formatMessage({
                  defaultMessage: `Change the owner of <strong>{collectionName}</strong> Collection`,
                }, { collectionName: collections[0].name })
              ) : (
                intl.formatMessage({
                  defaultMessage: `Change the owner of {collectionsCount} 
                  {collectionsCount, plural, one {Collection} other {Collections}}`,
                }, { collectionsCount: collections.length })
              )}
            </p>
          )}
          <Form className="change-owner-form">
            <FormGroup>
              <Select
                id="ownerSelect"
                inputId="ownerSelectInput"
                name="ownerUser"
                className="user-select"
                classNamePrefix="user-select"
                placeholder={
                  loading ?
                    intl.formatMessage({ defaultMessage: 'Loading...' }) :
                    intl.formatMessage({ defaultMessage: 'Select a new owner' })
                }
                noOptionsMessage={() => (intl.formatMessage({ defaultMessage: 'No users' }))}
                getOptionValue={option => option.email}
                getOptionLabel={option => option.name}
                components={{
                  Option: ShareModalInviteUserSelectItem,
                  MenuList: SelectVirtualMenuList,
                }}
                isDisabled={loading || saving}
                isLoading={loading}
                options={this.getUsersOptions()}
                onChange={this.handleUserChange}
                value={selectedUser}
                isOptionSelected={(option, value) => {
                  if (value[0]) {
                    return option.uuid === value[0].uuid;
                  }
                  return false;
                }}
              />
            </FormGroup>
            {mode === 'collections' && (
              <FormGroup check>
                <Label for="collectionFiles" check>
                  <Input
                    type="checkbox"
                    id="collectionFiles"
                    className="input-check check-s form-check-input no-margin"
                    value="1"
                    disabled={saving}
                    onClick={this.handleCollectionFilesChange}
                  />
                  {' '}
                  {intl.formatMessage({
                    defaultMessage: 'Change the owner of all files and Collections inside {collectionsCount, plural, one {this Collection} other {these Collections}}?',
                  }, { collectionsCount: collections.length })}
                </Label>
              </FormGroup>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <div className="buttons-group">
            <Button
              color="secondary"
              disabled={saving}
              onClick={handleClose}
            >
              {intl.formatMessage({ defaultMessage: 'Cancel' })}
            </Button>
            <Button
              color="primary"
              disabled={saving || !selectedUser || loading}
              onClick={this.handleUpdate}
            >
              <ProcessingSpinner show={saving} />
              {intl.formatMessage({ defaultMessage: 'Save' })}
              <ProcessingProgress
                show={saving}
                progress={{ total, done: updatedItems }}
              />
            </Button>
          </div>
        </ModalFooter>
      </ErrorBoundary>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  updateFileOnList: (file) => {
    dispatch(updateFilesOnFilesList([file]));
    dispatch(updateFilesOnSelectedFilesList([file]));
  },
  updateCollectionOnList: collection => (
    dispatch(updateCollectionOnCollectionsList(collection))
  ),
});

ChangeOwnerModal.propTypes = {
  intl: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  updateFileOnList: PropTypes.func.isRequired,
  updateCollectionOnList: PropTypes.func.isRequired,
  files: PropTypes.array,
  collections: PropTypes.array,
};

ChangeOwnerModal.defaultProps = {
  files: [],
  collections: [],
};

export default connect(null, mapDispatchToProps)(injectIntl(ChangeOwnerModal));
